import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MetricCard from '../components/MetricCard';
import { adminService } from '../services/adminService';
import {
  AdminSecurityAlertFilters,
  AdminSecurityAlertEvidence,
  AdminSecurityAlertListPayload,
  AdminSecurityAlertWorkflow,
  AdminSecurityClubHistory,
  AdminSecurityClubRiskPayload,
  AdminSecurityLoginProtectionOverview,
  AdminSecurityPlayerHistory,
  AdminSecurityOverview,
  AdminSecurityPlayerRisk,
  AdminSecurityRelationships,
  AdminSecurityRuleConfigHistory,
  AdminSecurityRuleEngine,
  AdminSecurityTableHistory,
  AdminSecurityTableRiskPayload,
} from '../types';

const INITIAL_FILTERS: Required<Omit<AdminSecurityAlertFilters, 'limit'>> = {
  search: '',
  severity: '',
  status: '',
  targetType: '',
};

const SECURITY_LIMIT = 18;
const SECURITY_SURFACE_LIMIT = 6;

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(parsed);
};

const formatScoreSource = (value: 'PERSISTED' | 'DERIVED'): string =>
  value === 'PERSISTED' ? 'Persistido' : 'Bootstrap';

const formatResolutionLabel = (value: 'RESOLVED' | 'DISMISSED'): string =>
  value === 'RESOLVED' ? 'Resolver' : 'Dispensar';

const formatRuleClass = (
  value: 'MULTI_ACCOUNT' | 'CHIP_DUMPING' | 'COLLUSION' | 'OPERATIONAL_PRESSURE'
): string => {
  switch (value) {
    case 'MULTI_ACCOUNT':
      return 'Multi-conta';
    case 'CHIP_DUMPING':
      return 'Chip dumping';
    case 'COLLUSION':
      return 'Colusao';
    default:
      return 'Pressao operacional';
  }
};

const formatFreshness = (value: number | null): string => {
  if (value === null || value === undefined) return '-';
  if (value < 60) return `${value} min`;
  if (value < 1440) return `${Math.floor(value / 60)} h`;
  return `${Math.floor(value / 1440)} d`;
};

const formatRetryAfter = (value: number): string => {
  if (!value || value <= 0) return '-';
  if (value < 60) return `${value}s`;
  if (value < 3600) return `${Math.ceil(value / 60)}m`;
  return `${Math.ceil(value / 3600)}h`;
};

const stringifyCompact = (value: unknown): string => {
  if (value === null || value === undefined) return '{}';
  try {
    const serialized = JSON.stringify(value);
    return serialized.length > 120 ? `${serialized.slice(0, 117)}...` : serialized;
  } catch {
    return '{}';
  }
};

const parseAlertEvidence = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];

  return value
    .filter((entry) => entry && typeof entry === 'object')
    .map((entry: any) => {
      const title = String(entry.title || entry.label || 'Evidencia').trim();
      const summary = String(entry.summary || entry.value || '').trim();
      const occurredAt = entry.occurred_at ? formatDateTime(String(entry.occurred_at)) : '-';
      return summary ? `${title}: ${summary} (${occurredAt})` : `${title} (${occurredAt})`;
    });
};

const formatWorkflowEvent = (event: AdminSecurityAlertWorkflow['timeline'][number]): string => {
  const parts = [event.event_type, event.actor_name || event.actor_email || event.actor_user_id || 'Admin'];

  if (event.previous_status || event.next_status) {
    parts.push(`${event.previous_status || '-'} -> ${event.next_status || '-'}`);
  }

  if (event.next_assigned_to_name || event.next_assigned_to_user_id) {
    parts.push(`owner: ${event.next_assigned_to_name || event.next_assigned_to_user_id}`);
  }

  if (event.reason) {
    parts.push(event.reason);
  }

  parts.push(formatDateTime(event.created_at));
  return parts.join(' | ');
};

const SecurityPage: React.FC = () => {
  const [overview, setOverview] = useState<AdminSecurityOverview | null>(null);
  const [ruleEngine, setRuleEngine] = useState<AdminSecurityRuleEngine | null>(null);
  const [loginProtection, setLoginProtection] = useState<AdminSecurityLoginProtectionOverview | null>(null);
  const [ruleHistory, setRuleHistory] = useState<AdminSecurityRuleConfigHistory | null>(null);
  const [tableRisk, setTableRisk] = useState<AdminSecurityTableRiskPayload | null>(null);
  const [clubRisk, setClubRisk] = useState<AdminSecurityClubRiskPayload | null>(null);
  const [alertsPayload, setAlertsPayload] = useState<AdminSecurityAlertListPayload | null>(null);
  const [playerRisk, setPlayerRisk] = useState<AdminSecurityPlayerRisk | null>(null);
  const [playerHistory, setPlayerHistory] = useState<AdminSecurityPlayerHistory | null>(null);
  const [playerRelationships, setPlayerRelationships] = useState<AdminSecurityRelationships | null>(null);
  const [alertWorkflow, setAlertWorkflow] = useState<AdminSecurityAlertWorkflow | null>(null);
  const [alertEvidence, setAlertEvidence] = useState<AdminSecurityAlertEvidence | null>(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [selectedRuleCode, setSelectedRuleCode] = useState('');
  const [selectedTableId, setSelectedTableId] = useState('');
  const [selectedClubId, setSelectedClubId] = useState('');
  const [selectedAlertId, setSelectedAlertId] = useState('');
  const [tableHistory, setTableHistory] = useState<AdminSecurityTableHistory | null>(null);
  const [clubHistory, setClubHistory] = useState<AdminSecurityClubHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [ruleEngineLoading, setRuleEngineLoading] = useState(true);
  const [loginProtectionLoading, setLoginProtectionLoading] = useState(true);
  const [ruleHistoryLoading, setRuleHistoryLoading] = useState(false);
  const [surfaceLoading, setSurfaceLoading] = useState(true);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [surfaceDetailLoading, setSurfaceDetailLoading] = useState(false);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<'assign' | 'resolve' | ''>('');
  const [error, setError] = useState('');
  const [ruleEngineError, setRuleEngineError] = useState('');
  const [loginProtectionError, setLoginProtectionError] = useState('');
  const [ruleHistoryError, setRuleHistoryError] = useState('');
  const [surfaceError, setSurfaceError] = useState('');
  const [alertsError, setAlertsError] = useState('');
  const [detailError, setDetailError] = useState('');
  const [surfaceDetailError, setSurfaceDetailError] = useState('');
  const [workflowError, setWorkflowError] = useState('');
  const [evidenceError, setEvidenceError] = useState('');
  const [actionError, setActionError] = useState('');
  const [ruleSaveError, setRuleSaveError] = useState('');
  const [assignReason, setAssignReason] = useState('');
  const [resolveReason, setResolveReason] = useState('');
  const [resolution, setResolution] = useState<'RESOLVED' | 'DISMISSED'>('RESOLVED');
  const [ruleSaveLoading, setRuleSaveLoading] = useState(false);
  const [ruleEnabled, setRuleEnabled] = useState(true);
  const [ruleSeverity, setRuleSeverity] = useState<'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'>('HIGH');
  const [ruleScoreDelta, setRuleScoreDelta] = useState('0');
  const [ruleConfidenceWeight, setRuleConfidenceWeight] = useState('0');
  const [ruleThresholdJson, setRuleThresholdJson] = useState('{}');
  const [ruleNotes, setRuleNotes] = useState('');
  const [ruleReason, setRuleReason] = useState('');

  const loadOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setOverview(await adminService.getSecurityOverview());
    } catch (nextError: any) {
      setError(nextError.message || 'Erro ao carregar o overview de seguranca');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRuleEngine = useCallback(async () => {
    setRuleEngineLoading(true);
    setRuleEngineError('');
    try {
      setRuleEngine(await adminService.getSecurityRuleEngine());
    } catch (nextError: any) {
      setRuleEngine(null);
      setRuleEngineError(nextError.message || 'Erro ao carregar o rule engine de seguranca');
    } finally {
      setRuleEngineLoading(false);
    }
  }, []);

  const loadLoginProtection = useCallback(async () => {
    setLoginProtectionLoading(true);
    setLoginProtectionError('');
    try {
      setLoginProtection(await adminService.getSecurityLoginProtectionOverview());
    } catch (nextError: any) {
      setLoginProtection(null);
      setLoginProtectionError(nextError.message || 'Erro ao carregar a protecao de login');
    } finally {
      setLoginProtectionLoading(false);
    }
  }, []);

  const loadRuleHistory = useCallback(async (ruleCode: string) => {
    setSelectedRuleCode(ruleCode);
    setRuleHistoryLoading(true);
    setRuleHistoryError('');
    setRuleSaveError('');
    setRuleReason('');
    try {
      const payload = await adminService.getSecurityRuleConfigHistory(ruleCode);
      setRuleHistory(payload);
      setRuleEnabled(payload.rule.enabled);
      setRuleSeverity(payload.rule.severity);
      setRuleScoreDelta(String(payload.rule.score_delta));
      setRuleConfidenceWeight(String(payload.rule.confidence_weight));
      setRuleThresholdJson(JSON.stringify(payload.rule.threshold_json || {}, null, 2));
      setRuleNotes(payload.rule.notes || '');
    } catch (nextError: any) {
      setRuleHistory(null);
      setRuleHistoryError(nextError.message || 'Erro ao carregar o historico da regra');
    } finally {
      setRuleHistoryLoading(false);
    }
  }, []);

  const loadRiskSurfaces = useCallback(async () => {
    setSurfaceLoading(true);
    setSurfaceError('');

    const [tableResult, clubResult] = await Promise.allSettled([
      adminService.getSecurityTableRisk(SECURITY_SURFACE_LIMIT),
      adminService.getSecurityClubRisk(SECURITY_SURFACE_LIMIT),
    ]);

    const nextErrors: string[] = [];

    if (tableResult.status === 'fulfilled') {
      setTableRisk(tableResult.value);
    } else {
      setTableRisk(null);
      nextErrors.push(tableResult.reason?.message || 'Erro ao carregar o risco por mesa');
    }

    if (clubResult.status === 'fulfilled') {
      setClubRisk(clubResult.value);
    } else {
      setClubRisk(null);
      nextErrors.push(clubResult.reason?.message || 'Erro ao carregar o risco por club');
    }

    setSurfaceError(nextErrors.join(' '));
    setSurfaceLoading(false);
  }, []);

  const loadAlerts = useCallback(async (nextFilters: typeof INITIAL_FILTERS = INITIAL_FILTERS) => {
    setAlertsLoading(true);
    setAlertsError('');
    try {
      setAlertsPayload(await adminService.listSecurityAlerts({ ...nextFilters, limit: SECURITY_LIMIT }));
    } catch (nextError: any) {
      setAlertsError(nextError.message || 'Erro ao carregar a fila de alertas');
    } finally {
      setAlertsLoading(false);
    }
  }, []);

  const loadPlayerDetails = useCallback(async (userId: string) => {
    setSelectedPlayerId(userId);
    setDetailLoading(true);
    setDetailError('');

    const [riskResult, historyResult, relationshipsResult] = await Promise.allSettled([
      adminService.getSecurityPlayerRisk(userId),
      adminService.getSecurityPlayerHistory(userId),
      adminService.getSecurityPlayerRelationships(userId),
    ]);

    const nextErrors: string[] = [];

    if (riskResult.status === 'fulfilled') {
      setPlayerRisk(riskResult.value);
    } else {
      setPlayerRisk(null);
      nextErrors.push(riskResult.reason?.message || 'Erro ao carregar o score do jogador');
    }

    if (historyResult.status === 'fulfilled') {
      setPlayerHistory(historyResult.value);
    } else {
      setPlayerHistory(null);
      nextErrors.push(historyResult.reason?.message || 'Erro ao carregar o historico do jogador');
    }

    if (relationshipsResult.status === 'fulfilled') {
      setPlayerRelationships(relationshipsResult.value);
    } else {
      setPlayerRelationships(null);
      nextErrors.push(relationshipsResult.reason?.message || 'Erro ao carregar as relacoes do jogador');
    }

    setDetailError(nextErrors.join(' '));
    setDetailLoading(false);
  }, []);

  const loadTableHistory = useCallback(async (tableId: string) => {
    setSelectedTableId(tableId);
    setSurfaceDetailLoading(true);
    setSurfaceDetailError('');
    try {
      setTableHistory(await adminService.getSecurityTableHistory(tableId));
    } catch (nextError: any) {
      setTableHistory(null);
      setSurfaceDetailError(nextError.message || 'Erro ao carregar o historico da mesa');
    } finally {
      setSurfaceDetailLoading(false);
    }
  }, []);

  const loadClubHistory = useCallback(async (clubId: string) => {
    setSelectedClubId(clubId);
    setSurfaceDetailLoading(true);
    setSurfaceDetailError('');
    try {
      setClubHistory(await adminService.getSecurityClubHistory(clubId));
    } catch (nextError: any) {
      setClubHistory(null);
      setSurfaceDetailError(nextError.message || 'Erro ao carregar o historico do club');
    } finally {
      setSurfaceDetailLoading(false);
    }
  }, []);

  const loadAlertWorkflow = useCallback(async (alertId: string) => {
    setSelectedAlertId(alertId);
    setWorkflowLoading(true);
    setWorkflowError('');
    try {
      setAlertWorkflow(await adminService.getSecurityAlertWorkflow(alertId));
    } catch (nextError: any) {
      setAlertWorkflow(null);
      setWorkflowError(nextError.message || 'Erro ao carregar o workflow do alerta');
    } finally {
      setWorkflowLoading(false);
    }
  }, []);

  const loadAlertEvidence = useCallback(async (alertId: string) => {
    setEvidenceLoading(true);
    setEvidenceError('');
    try {
      setAlertEvidence(await adminService.getSecurityAlertEvidence(alertId));
    } catch (nextError: any) {
      setAlertEvidence(null);
      setEvidenceError(nextError.message || 'Erro ao carregar o bundle de evidencia do alerta');
    } finally {
      setEvidenceLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadOverview();
    void loadRuleEngine();
    void loadLoginProtection();
    void loadRiskSurfaces();
    void loadAlerts(INITIAL_FILTERS);
  }, [loadAlerts, loadLoginProtection, loadOverview, loadRiskSurfaces, loadRuleEngine]);

  useEffect(() => {
    if (!selectedPlayerId && overview?.top_risk_players?.length) {
      void loadPlayerDetails(overview.top_risk_players[0].user_id);
    }
  }, [loadPlayerDetails, overview, selectedPlayerId]);

  useEffect(() => {
    if (!selectedRuleCode && ruleEngine?.rules.length) {
      void loadRuleHistory(ruleEngine.rules[0].rule_code);
    }
  }, [loadRuleHistory, ruleEngine, selectedRuleCode]);

  useEffect(() => {
    if (!selectedAlertId && alertsPayload?.items.length) {
      void loadAlertWorkflow(alertsPayload.items[0].id);
      void loadAlertEvidence(alertsPayload.items[0].id);
    }
  }, [alertsPayload, loadAlertEvidence, loadAlertWorkflow, selectedAlertId]);

  useEffect(() => {
    if (!selectedTableId && tableRisk?.items.length) {
      void loadTableHistory(tableRisk.items[0].table_id);
    }
  }, [loadTableHistory, selectedTableId, tableRisk]);

  useEffect(() => {
    if (!selectedClubId && clubRisk?.items.length) {
      void loadClubHistory(clubRisk.items[0].club_id);
    }
  }, [clubRisk, loadClubHistory, selectedClubId]);

  const summary = overview?.summary;
  const metricCards = useMemo(
    () =>
      summary
        ? [
            { eyebrow: 'Security', title: 'Alertas abertos', value: String(summary.total_open_alerts), tone: 'rose' as const },
            { eyebrow: 'Criticos', title: 'Fila critica', value: String(summary.critical_open_alerts), tone: 'rose' as const },
            { eyebrow: 'Watchlist', title: 'Jogadores em watchlist', value: String(summary.watchlist_players), tone: 'amber' as const },
            { eyebrow: 'Escalada', title: 'Jogadores escalados', value: String(summary.escalated_players), tone: 'blue' as const },
            { eyebrow: 'Score', title: 'Risco medio', value: String(summary.average_risk_score), tone: 'slate' as const },
            { eyebrow: 'Ownership', title: 'Sem responsavel', value: String(summary.alerts_without_assignee), tone: 'slate' as const },
          ]
        : [],
    [summary]
  );

  const loginProtectionCards = useMemo(
    () =>
      loginProtection?.summary
        ? [
            {
              eyebrow: 'Protecao',
              title: 'Chaves monitoradas',
              value: String(loginProtection.summary.tracked_keys),
              tone: 'blue' as const,
            },
            {
              eyebrow: 'Bloqueio',
              title: 'Chaves bloqueadas',
              value: String(loginProtection.summary.blocked_keys),
              tone: 'rose' as const,
            },
            {
              eyebrow: 'CPF',
              title: 'Chaves CPF',
              value: String(loginProtection.summary.cpf_keys),
              tone: 'amber' as const,
            },
            {
              eyebrow: 'IP',
              title: 'Chaves IP',
              value: String(loginProtection.summary.ip_keys),
              tone: 'slate' as const,
            },
            {
              eyebrow: 'Falhas',
              title: 'Tentativas falhas',
              value: String(loginProtection.summary.failed_attempts_total),
              tone: 'rose' as const,
            },
            {
              eyebrow: 'Cooldown',
              title: 'Maior espera',
              value: formatRetryAfter(loginProtection.summary.max_retry_after_seconds),
              tone: 'blue' as const,
            },
          ]
        : [],
    [loginProtection]
  );

  const handleFilterChange = (field: keyof typeof INITIAL_FILTERS, value: string) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void loadAlerts(filters);
  };

  const handleClear = () => {
    setFilters(INITIAL_FILTERS);
    void loadAlerts(INITIAL_FILTERS);
  };

  const handleRefresh = async () => {
    await Promise.all([
      loadOverview(),
      loadRuleEngine(),
      loadLoginProtection(),
      selectedRuleCode ? loadRuleHistory(selectedRuleCode) : Promise.resolve(),
      loadRiskSurfaces(),
      loadAlerts(filters),
      selectedPlayerId ? loadPlayerDetails(selectedPlayerId) : Promise.resolve(),
      selectedTableId ? loadTableHistory(selectedTableId) : Promise.resolve(),
      selectedClubId ? loadClubHistory(selectedClubId) : Promise.resolve(),
      selectedAlertId ? loadAlertWorkflow(selectedAlertId) : Promise.resolve(),
      selectedAlertId ? loadAlertEvidence(selectedAlertId) : Promise.resolve(),
    ]);
  };

  const handleRuleSave = async () => {
    if (!selectedRuleCode) return;
    const reason = ruleReason.trim();
    if (!reason) {
      setRuleSaveError('Informe o motivo para governar esta regra.');
      return;
    }

    let thresholdJson: Record<string, unknown>;
    try {
      thresholdJson = ruleThresholdJson.trim() ? JSON.parse(ruleThresholdJson) : {};
    } catch {
      setRuleSaveError('Threshold JSON invalido para esta regra.');
      return;
    }

    setRuleSaveLoading(true);
    setRuleSaveError('');

    try {
      const payload = await adminService.overrideSecurityRuleConfig(selectedRuleCode, {
        enabled: ruleEnabled,
        severity: ruleSeverity,
        scoreDelta: Number(ruleScoreDelta),
        confidenceWeight: Number(ruleConfidenceWeight),
        thresholdJson,
        notes: ruleNotes.trim() || null,
        reason,
      });

      setRuleHistory(payload);
      setRuleEnabled(payload.rule.enabled);
      setRuleSeverity(payload.rule.severity);
      setRuleScoreDelta(String(payload.rule.score_delta));
      setRuleConfidenceWeight(String(payload.rule.confidence_weight));
      setRuleThresholdJson(JSON.stringify(payload.rule.threshold_json || {}, null, 2));
      setRuleNotes(payload.rule.notes || '');
      setRuleReason('');

      await Promise.all([
        loadRuleEngine(),
        loadOverview(),
        loadAlerts(filters),
      ]);
    } catch (nextError: any) {
      setRuleSaveError(nextError.message || 'Erro ao governar a regra');
    } finally {
      setRuleSaveLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!alertWorkflow) return;
    const reason = assignReason.trim();
    if (!reason) {
      setActionError('Informe o motivo para assumir este alerta.');
      return;
    }

    setActionLoading('assign');
    setActionError('');

    try {
      const payload = await adminService.assignSecurityAlert(alertWorkflow.alert.id, { reason });
      setAlertWorkflow(payload);
      setSelectedAlertId(payload.alert.id);
      setAssignReason('');

      await Promise.all([
        loadOverview(),
        loadAlerts(filters),
        payload.alert.player_user_id ? loadPlayerDetails(payload.alert.player_user_id) : Promise.resolve(),
        loadAlertEvidence(payload.alert.id),
      ]);
    } catch (nextError: any) {
      setActionError(nextError.message || 'Erro ao atribuir alerta');
    } finally {
      setActionLoading('');
    }
  };

  const handleResolve = async () => {
    if (!alertWorkflow) return;
    const reason = resolveReason.trim();
    if (!reason) {
      setActionError('Informe o motivo para encerrar este alerta.');
      return;
    }

    setActionLoading('resolve');
    setActionError('');

    try {
      const payload = await adminService.resolveSecurityAlert(alertWorkflow.alert.id, { reason, resolution });
      setAlertWorkflow(payload);
      setSelectedAlertId(payload.alert.id);
      setResolveReason('');

      await Promise.all([
        loadOverview(),
        loadAlerts(filters),
        payload.alert.player_user_id ? loadPlayerDetails(payload.alert.player_user_id) : Promise.resolve(),
        loadAlertEvidence(payload.alert.id),
      ]);
    } catch (nextError: any) {
      setActionError(nextError.message || 'Erro ao encerrar alerta');
    } finally {
      setActionLoading('');
    }
  };

  const workflowEvidence = parseAlertEvidence(alertWorkflow?.alert.evidence_json);

  return (
    <div className="admin-page-stack">
      <section className="admin-hero-card">
        <div>
          <p className="admin-section-eyebrow">Seguranca</p>
          <h1>Fila de risco com relacoes e workflow auditado</h1>
          <p>Agora a camada cruza score, relacoes entre jogadores e workflow de atribuicao ou encerramento com trilha administrativa.</p>
        </div>
        <button type="button" className="admin-secondary-button" onClick={() => { void handleRefresh(); }} disabled={loading || ruleEngineLoading || loginProtectionLoading || ruleHistoryLoading || ruleSaveLoading || surfaceLoading || alertsLoading || detailLoading || surfaceDetailLoading || workflowLoading || evidenceLoading}>
          {loading || ruleEngineLoading || ruleHistoryLoading || ruleSaveLoading || surfaceLoading || alertsLoading || detailLoading || surfaceDetailLoading || workflowLoading || evidenceLoading ? 'Atualizando...' : 'Atualizar'}
        </button>
      </section>

      {error ? <div className="admin-banner admin-banner--error">{error}</div> : null}
      {ruleEngineError ? <div className="admin-banner admin-banner--error">{ruleEngineError}</div> : null}
      {loginProtectionError ? <div className="admin-banner admin-banner--error">{loginProtectionError}</div> : null}
      {ruleHistoryError ? <div className="admin-banner admin-banner--error">{ruleHistoryError}</div> : null}
      {ruleSaveError ? <div className="admin-banner admin-banner--error">{ruleSaveError}</div> : null}
      {surfaceError ? <div className="admin-banner admin-banner--error">{surfaceError}</div> : null}
      {alertsError ? <div className="admin-banner admin-banner--error">{alertsError}</div> : null}
      {detailError ? <div className="admin-banner admin-banner--error">{detailError}</div> : null}
      {surfaceDetailError ? <div className="admin-banner admin-banner--error">{surfaceDetailError}</div> : null}
      {workflowError ? <div className="admin-banner admin-banner--error">{workflowError}</div> : null}
      {evidenceError ? <div className="admin-banner admin-banner--error">{evidenceError}</div> : null}
      {actionError ? <div className="admin-banner admin-banner--error">{actionError}</div> : null}

      <section className="admin-metrics-grid">{metricCards.map((card) => <MetricCard key={card.title} {...card} />)}</section>

      <section className="admin-section-card">
        <div className="admin-section-card__header">
          <div>
            <p className="admin-section-eyebrow">Login protection</p>
            <h2>Fila de bloqueio por login</h2>
          </div>
          <span className="admin-chip">
            {loginProtectionLoading ? 'Atualizando' : `${loginProtection?.summary.blocked_keys || 0} bloqueio(s) ativo(s)`}
          </span>
        </div>
        <div className="admin-kpi-grid">
          {loginProtectionCards.map((card) => (
            <MetricCard key={card.title} {...card} />
          ))}
        </div>
        <div className="admin-note-stack">
          {loginProtection?.notes?.map((note, index) => (
            <div key={`login-protection-note-${index}`} className="admin-note-chip">
              {note}
            </div>
          ))}
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Identificador</th>
                <th>Tipo</th>
                <th>Falhas</th>
                <th>Lockouts</th>
                <th>Status</th>
                <th>Bloqueado ate</th>
                <th>Ultima falha</th>
                <th>Retry</th>
              </tr>
            </thead>
            <tbody>
              {loginProtection?.items.length ? loginProtection.items.map((item) => (
                <tr key={`${item.login_key_type}-${item.login_key_masked}-${item.blocked_until || 'na'}`}>
                  <td><strong>{item.login_key_masked}</strong><span>{item.status}</span></td>
                  <td>{item.login_key_type}</td>
                  <td><strong>{item.failed_attempts}</strong></td>
                  <td><strong>{item.lockout_count}</strong></td>
                  <td><strong>{item.status}</strong><span>{item.retry_after_seconds > 0 ? 'Bloqueado' : 'Monitorando'}</span></td>
                  <td>{formatDateTime(item.blocked_until)}</td>
                  <td>{formatDateTime(item.last_failed_at)}</td>
                  <td>{formatRetryAfter(item.retry_after_seconds)}</td>
                </tr>
              )) : (
                <tr><td colSpan={8}>{loginProtectionLoading ? 'Carregando protecao de login...' : 'Nenhuma chave de login esta em observacao no momento.'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="admin-section-card">
        <div className="admin-section-card__header">
          <div>
            <p className="admin-section-eyebrow">Rule engine</p>
            <h2>Coverage, freshness e calibracao</h2>
          </div>
          <span className="admin-chip">
            {ruleEngineLoading ? 'Atualizando' : `${ruleEngine?.summary.persisted_hits_7d || 0} hit(s) em 7d`}
          </span>
        </div>
        <div className="admin-kpi-grid">
          <article className="admin-kpi-item"><span>Regras ativas</span><strong>{ruleEngine?.summary.enabled_rules || 0}</strong></article>
          <article className="admin-kpi-item"><span>Confidence media</span><strong>{ruleEngine?.summary.avg_confidence_score || 0}</strong></article>
          <article className="admin-kpi-item"><span>Coverage player</span><strong>{ruleEngine?.coverage.player_coverage_pct || 0}%</strong></article>
          <article className="admin-kpi-item"><span>Coverage table</span><strong>{ruleEngine?.coverage.table_coverage_pct || 0}%</strong></article>
          <article className="admin-kpi-item"><span>Coverage club</span><strong>{ruleEngine?.coverage.club_coverage_pct || 0}%</strong></article>
          <article className="admin-kpi-item"><span>Fresh 24h</span><strong>{ruleEngine?.freshness.fresh_hits_24h || 0}</strong></article>
          <article className="admin-kpi-item"><span>Bundles 24h</span><strong>{ruleEngine?.summary.bundles_updated_24h || 0}</strong></article>
          <article className="admin-kpi-item"><span>Alertas com hit</span><strong>{ruleEngine?.summary.active_alerts_with_hits || 0}</strong></article>
        </div>
        <div className="admin-note-stack">
          {ruleEngine?.classes.map((item) => (
            <div key={item.rule_class} className="admin-note-chip">
              {formatRuleClass(item.rule_class)}: {item.enabled_rules}/{item.configured_rules} regra(s) ativa(s), {item.recent_hits} hit(s), confidence media {item.avg_confidence_score}
            </div>
          ))}
        </div>
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Thresholds</p>
              <h2>Calibracao por regra</h2>
            </div>
            <span className="admin-chip">{ruleEngine?.rules.length || 0} regra(s)</span>
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Regra</th>
                  <th>Classe</th>
                  <th>Scope</th>
                  <th>Threshold</th>
                  <th>Weight</th>
                  <th>Origem</th>
                  <th>Governanca</th>
                </tr>
              </thead>
              <tbody>
                {ruleEngine?.rules.length ? ruleEngine.rules.map((rule) => (
                  <tr key={rule.rule_code} className={selectedRuleCode === rule.rule_code ? 'admin-table__row--selected' : ''}>
                    <td><strong>{rule.rule_code}</strong><span>{rule.enabled ? 'ENABLED' : 'DISABLED'}</span></td>
                    <td>{formatRuleClass(rule.rule_class)}</td>
                    <td><strong>{rule.entity_type}</strong><span>{rule.severity}</span></td>
                    <td>{Object.keys(rule.threshold_json).length ? JSON.stringify(rule.threshold_json) : '-'}</td>
                    <td><strong>{rule.confidence_weight}</strong><span>delta {rule.score_delta}</span></td>
                    <td><strong>{rule.source === 'PERSISTED_RULE_CONFIG' ? 'Persistida' : 'Default'}</strong><span>{formatDateTime(rule.updated_at)}</span></td>
                    <td><button type="button" className="admin-link-chip admin-link-chip--button" onClick={() => void loadRuleHistory(rule.rule_code)} disabled={ruleHistoryLoading && selectedRuleCode === rule.rule_code}>{ruleHistoryLoading && selectedRuleCode === rule.rule_code ? 'Abrindo...' : 'Governar'}</button></td>
                  </tr>
                )) : (
                  <tr><td colSpan={7}>{ruleEngineLoading ? 'Carregando calibracao...' : 'Nenhuma regra configurada foi encontrada.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </article>

        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Recent hits</p>
              <h2>Ultimos sinais persistidos</h2>
            </div>
            <span className="admin-chip">{ruleEngine?.recent_hits.length || 0} item(ns)</span>
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Rule</th>
                  <th>Classe</th>
                  <th>Alvo</th>
                  <th>Confidence</th>
                  <th>Freshness</th>
                  <th>Threshold x observado</th>
                </tr>
              </thead>
              <tbody>
                {ruleEngine?.recent_hits.length ? ruleEngine.recent_hits.map((hit) => (
                  <tr key={`${hit.rule_code}-${hit.entity_id}-${hit.detected_at || 'na'}`}>
                    <td><strong>{hit.rule_code}</strong><span>{hit.severity} | {hit.score_delta} pts</span></td>
                    <td><strong>{formatRuleClass(hit.rule_class)}</strong><span>{hit.origin === 'PERSISTED_RULE_CONFIG' ? 'Persistida' : 'Default'}</span></td>
                    <td><strong>{hit.entity_type}</strong><span>{hit.coverage_scope}</span></td>
                    <td><strong>{hit.confidence_score}</strong><span>{hit.confidence_label}</span></td>
                    <td><strong>{formatFreshness(hit.freshness_minutes)}</strong><span>{formatDateTime(hit.detected_at)}</span></td>
                    <td><strong>{hit.threshold_summary}</strong><span>{hit.observed_summary}</span></td>
                  </tr>
                )) : (
                  <tr><td colSpan={6}>{ruleEngineLoading ? 'Carregando rule hits...' : 'Nenhum rule hit persistido apareceu no recorte recente.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Governanca</p>
              <h2>{ruleHistory?.rule.rule_code || 'Selecione uma regra'}</h2>
            </div>
            <span className="admin-chip">
              {ruleHistoryLoading ? 'Carregando' : ruleHistory?.rule.enabled ? 'ENABLED' : 'DISABLED'}
            </span>
          </div>

          {ruleHistory ? (
            <>
              <div className="admin-kpi-grid">
                <article className="admin-kpi-item"><span>Classe</span><strong>{formatRuleClass(ruleHistory.rule.rule_class)}</strong></article>
                <article className="admin-kpi-item"><span>Scope</span><strong>{ruleHistory.rule.entity_type}</strong></article>
                <article className="admin-kpi-item"><span>Delta</span><strong>{ruleHistory.rule.score_delta}</strong></article>
                <article className="admin-kpi-item"><span>Weight</span><strong>{ruleHistory.rule.confidence_weight}</strong></article>
              </div>

              <div className="admin-filter-grid">
                <label className="admin-filter-field">
                  <span>Enabled</span>
                  <select className="admin-search-input" value={ruleEnabled ? 'true' : 'false'} onChange={(event) => setRuleEnabled(event.target.value === 'true')}>
                    <option value="true">ENABLED</option>
                    <option value="false">DISABLED</option>
                  </select>
                </label>
                <label className="admin-filter-field">
                  <span>Severity</span>
                  <select className="admin-search-input" value={ruleSeverity} onChange={(event) => setRuleSeverity(event.target.value as 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW')}>
                    <option value="CRITICAL">CRITICAL</option>
                    <option value="HIGH">HIGH</option>
                    <option value="MEDIUM">MEDIUM</option>
                    <option value="LOW">LOW</option>
                  </select>
                </label>
                <label className="admin-filter-field">
                  <span>Score delta</span>
                  <input className="admin-search-input" value={ruleScoreDelta} onChange={(event) => setRuleScoreDelta(event.target.value)} />
                </label>
                <label className="admin-filter-field">
                  <span>Confidence weight</span>
                  <input className="admin-search-input" value={ruleConfidenceWeight} onChange={(event) => setRuleConfidenceWeight(event.target.value)} />
                </label>
                <label className="admin-filter-field">
                  <span>Threshold JSON</span>
                  <textarea className="admin-search-input" rows={8} value={ruleThresholdJson} onChange={(event) => setRuleThresholdJson(event.target.value)} />
                </label>
                <label className="admin-filter-field">
                  <span>Notas</span>
                  <textarea className="admin-search-input" rows={4} value={ruleNotes} onChange={(event) => setRuleNotes(event.target.value)} placeholder="Observacao operacional opcional para esta calibracao." />
                </label>
                <label className="admin-filter-field">
                  <span>Motivo auditado</span>
                  <textarea className="admin-search-input" rows={4} value={ruleReason} onChange={(event) => setRuleReason(event.target.value)} placeholder="Explique por que esta governanca esta sendo aplicada." />
                </label>
              </div>

              <div className="admin-filter-actions">
                <button type="button" className="admin-secondary-button" onClick={() => void loadRuleHistory(ruleHistory.rule.rule_code)} disabled={ruleHistoryLoading || ruleSaveLoading}>
                  {ruleHistoryLoading ? 'Recarregando...' : 'Recarregar regra'}
                </button>
                <button type="button" className="admin-secondary-button" onClick={() => void handleRuleSave()} disabled={ruleSaveLoading}>
                  {ruleSaveLoading ? 'Salvando...' : 'Salvar governanca'}
                </button>
              </div>
            </>
          ) : (
            <div className="admin-note-chip">{ruleHistoryLoading ? 'Carregando regra...' : 'Selecione uma regra da tabela para abrir a governanca auditada.'}</div>
          )}
        </article>

        <article className="admin-section-card">
          <div className="admin-section-card__header">
            <div>
              <p className="admin-section-eyebrow">Versionamento</p>
              <h2>Before e after por regra</h2>
            </div>
            <span className="admin-chip">{ruleHistory?.versions.length || 0} versao(oes)</span>
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Versao</th>
                  <th>Tipo</th>
                  <th>Responsavel</th>
                  <th>Motivo</th>
                  <th>Before</th>
                  <th>After</th>
                  <th>Quando</th>
                </tr>
              </thead>
              <tbody>
                {ruleHistory?.versions.length ? ruleHistory.versions.map((item) => (
                  <tr key={item.id}>
                    <td><strong>v{item.version_number}</strong><span>{item.rule_code}</span></td>
                    <td>{item.change_type}</td>
                    <td><strong>{item.changed_by_name || 'Sistema'}</strong><span>{item.changed_by_email || item.changed_by_user_id || '-'}</span></td>
                    <td>{item.reason || '-'}</td>
                    <td>{stringifyCompact(item.before_json)}</td>
                    <td>{stringifyCompact(item.after_json)}</td>
                    <td>{formatDateTime(item.created_at)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={7}>{ruleHistoryLoading ? 'Carregando historico...' : 'Nenhuma versao persistida foi encontrada para a regra em foco.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="admin-note-stack">
            {(ruleHistory?.notes || []).map((note, index) => (
              <div key={`rule-history-note-${index}`} className="admin-note-chip">{note}</div>
            ))}
          </div>
        </article>
      </section>

      <section className="admin-section-card">
        <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Filtros</p><h2>Refino da fila de alertas</h2></div><span className="admin-chip">Limite atual: {alertsPayload?.applied_filters.limit || SECURITY_LIMIT}</span></div>
        <form onSubmit={handleSubmit}>
          <div className="admin-filter-grid">
            <label className="admin-filter-field"><span>Busca</span><input className="admin-search-input" value={filters.search} onChange={(event) => handleFilterChange('search', event.target.value)} placeholder="Titulo, jogador, club ou mesa" /></label>
            <label className="admin-filter-field"><span>Severidade</span><select className="admin-search-input" value={filters.severity} onChange={(event) => handleFilterChange('severity', event.target.value)}><option value="">Todas</option><option value="CRITICAL">CRITICAL</option><option value="HIGH">HIGH</option><option value="MEDIUM">MEDIUM</option><option value="LOW">LOW</option></select></label>
            <label className="admin-filter-field"><span>Status</span><select className="admin-search-input" value={filters.status} onChange={(event) => handleFilterChange('status', event.target.value)}><option value="">Todos</option><option value="OPEN">OPEN</option><option value="INVESTIGATING">INVESTIGATING</option><option value="ESCALATED">ESCALATED</option><option value="RESOLVED">RESOLVED</option><option value="DISMISSED">DISMISSED</option></select></label>
            <label className="admin-filter-field"><span>Alvo</span><select className="admin-search-input" value={filters.targetType} onChange={(event) => handleFilterChange('targetType', event.target.value)}><option value="">Todos</option><option value="PLAYER">PLAYER</option><option value="CLUB">CLUB</option><option value="TABLE">TABLE</option></select></label>
          </div>
          <div className="admin-filter-actions"><button type="button" className="admin-secondary-button" onClick={handleClear} disabled={alertsLoading}>Limpar</button><button type="submit" className="admin-secondary-button" disabled={alertsLoading}>{alertsLoading ? 'Filtrando...' : 'Aplicar filtros'}</button></div>
        </form>
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Scoreboard</p><h2>Jogadores com maior risco</h2></div><span className="admin-chip">{overview?.top_risk_players.length || 0} jogador(es)</span></div>
          <div className="admin-table-wrap"><table className="admin-table"><thead><tr><th>Jogador</th><th>Score</th><th>Status</th><th>Alertas</th><th>Clubs</th><th>Detalhe</th></tr></thead><tbody>{overview?.top_risk_players.length ? overview.top_risk_players.map((player) => (<tr key={player.user_id} className={selectedPlayerId === player.user_id ? 'admin-table__row--selected' : ''}><td><strong>{player.user_name}</strong><span>{player.email || '-'}</span></td><td><strong>{player.score_total}</strong><span>{player.risk_level}</span></td><td><strong>{player.status}</strong><span>{formatScoreSource(player.score_source)}</span></td><td><strong>{player.open_alerts_count}</strong><span>{player.critical_alerts_count} critico(s)</span></td><td>{player.clubs_count}</td><td><button type="button" className="admin-link-chip admin-link-chip--button" onClick={() => void loadPlayerDetails(player.user_id)} disabled={detailLoading && selectedPlayerId === player.user_id}>{detailLoading && selectedPlayerId === player.user_id ? 'Carregando...' : 'Inspecionar'}</button></td></tr>)) : <tr><td colSpan={6}>Nenhum jogador entrou na fila de risco ainda.</td></tr>}</tbody></table></div>
        </article>
        <article className="admin-section-card">
          <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Alertas</p><h2>Fila atual de triagem</h2></div><span className="admin-chip">{alertsPayload?.summary.total_items || 0} item(ns)</span></div>
          <div className="admin-kpi-grid"><article className="admin-kpi-item"><span>Abertos</span><strong>{alertsPayload?.summary.open_items || 0}</strong></article><article className="admin-kpi-item"><span>Criticos</span><strong>{alertsPayload?.summary.critical_items || 0}</strong></article><article className="admin-kpi-item"><span>Persistidos</span><strong>{alertsPayload?.summary.persisted_items || 0}</strong></article><article className="admin-kpi-item"><span>Bootstrap</span><strong>{alertsPayload?.summary.derived_items || 0}</strong></article></div>
          <div className="admin-table-wrap"><table className="admin-table"><thead><tr><th>Titulo</th><th>Severidade</th><th>Alvo</th><th>Status</th><th>Aberto</th><th>Workflow</th></tr></thead><tbody>{alertsPayload?.items.length ? alertsPayload.items.map((alert) => (<tr key={alert.id} className={selectedAlertId === alert.id ? 'admin-table__row--selected' : ''}><td><strong>{alert.title}</strong><span>{alert.category}</span></td><td><strong>{alert.severity}</strong><span>Score {alert.risk_score}</span></td><td><strong>{alert.target_type}</strong><span>{alert.player_name || alert.club_name || alert.table_name || '-'}</span></td><td><strong>{alert.status}</strong><span>{alert.assigned_to_name || 'Sem responsavel'}</span></td><td>{formatDateTime(alert.opened_at)}</td><td><button type="button" className="admin-link-chip admin-link-chip--button" onClick={() => { void loadAlertWorkflow(alert.id); void loadAlertEvidence(alert.id); }} disabled={workflowLoading && selectedAlertId === alert.id}>{workflowLoading && selectedAlertId === alert.id ? 'Carregando...' : 'Abrir'}</button></td></tr>)) : <tr><td colSpan={6}>Nenhum alerta encontrado para o recorte atual.</td></tr>}</tbody></table></div>
        </article>
      </section>

      <section className="admin-grid admin-grid--two">
        {playerRisk ? (
          <article className="admin-section-card">
            <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Jogador em foco</p><h2>{playerRisk.profile.user_name}</h2></div><span className="admin-chip">{playerRisk.score.risk_level}</span></div>
            <div className="admin-kpi-grid"><article className="admin-kpi-item"><span>Score total</span><strong>{playerRisk.score.total_score}</strong></article><article className="admin-kpi-item"><span>Status</span><strong>{playerRisk.score.status}</strong></article><article className="admin-kpi-item"><span>Alertas abertos</span><strong>{playerRisk.score.open_alerts_count}</strong></article><article className="admin-kpi-item"><span>Criticos</span><strong>{playerRisk.score.critical_alerts_count}</strong></article></div>
            <div className="admin-detail-grid"><div className="admin-detail-pair"><span>Email</span><strong>{playerRisk.profile.email || '-'}</strong></div><div className="admin-detail-pair"><span>CPF</span><strong>{playerRisk.profile.cpf || '-'}</strong></div><div className="admin-detail-pair"><span>Role</span><strong>{playerRisk.profile.role}</strong></div><div className="admin-detail-pair"><span>Fonte</span><strong>{formatScoreSource(playerRisk.score.score_source)}</strong></div><div className="admin-detail-pair"><span>Ultimo evento</span><strong>{formatDateTime(playerRisk.score.last_event_at)}</strong></div><div className="admin-detail-pair"><span>Ultima revisao</span><strong>{formatDateTime(playerRisk.score.reviewed_at)}</strong></div><div className="admin-detail-pair"><span>Revisado por</span><strong>{playerRisk.score.reviewed_by_name || '-'}</strong></div><div className="admin-detail-pair"><span>Banido</span><strong>{playerRisk.profile.is_banned ? 'SIM' : 'NAO'}</strong></div></div>
            <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Sinais</p><h2>Componentes do score</h2></div><span className="admin-chip">{playerRisk.signals.length} sinal(is)</span></div>
            <div className="admin-table-wrap"><table className="admin-table"><thead><tr><th>Sinal</th><th>Pontos</th><th>Valor</th><th>Descricao</th></tr></thead><tbody>{playerRisk.signals.length ? playerRisk.signals.map((signal) => (<tr key={signal.key}><td>{signal.label}</td><td><strong>{signal.points}</strong></td><td>{signal.current_value}</td><td>{signal.description}</td></tr>)) : <tr><td colSpan={4}>Nenhum sinal consolidado para este jogador.</td></tr>}</tbody></table></div>
            <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Clubs relacionados</p><h2>Contexto operacional</h2></div></div>
            <div className="admin-table-wrap"><table className="admin-table"><thead><tr><th>Club</th><th>Status</th><th>Ultima atividade</th><th>Alertas abertos</th></tr></thead><tbody>{playerRisk.related_clubs.length ? playerRisk.related_clubs.map((club) => (<tr key={club.club_id}><td><strong>{club.club_name}</strong><span>{club.club_code}</span></td><td>{club.membership_status || '-'}</td><td>{formatDateTime(club.last_activity_at)}</td><td>{club.open_alerts_count}</td></tr>)) : <tr><td colSpan={4}>Nenhum club relacionado foi consolidado para este jogador.</td></tr>}</tbody></table></div>
            <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Evidencias</p><h2>Trilha minima antes do workflow</h2></div></div>
            <div className="admin-note-stack">{playerRisk.evidence.length ? playerRisk.evidence.map((entry) => (<div key={entry.key} className="admin-note-chip">{entry.title}: {entry.summary} ({formatDateTime(entry.occurred_at)})</div>)) : <div className="admin-note-chip">Nenhuma evidencia agregada para este jogador.</div>}</div>
            <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Historico</p><h2>Score do jogador nos ultimos 7 dias</h2></div><span className="admin-chip">{playerHistory?.current_score.total_score || 0}</span></div>
            <div className="admin-table-wrap"><table className="admin-table"><thead><tr><th>Bucket</th><th>Score</th><th>Alertas</th><th>Workflow</th><th>Incidents</th><th>Sinal</th></tr></thead><tbody>{playerHistory?.items.length ? playerHistory.items.map((item) => (<tr key={item.bucket_start}><td>{item.bucket_label}</td><td><strong>{item.score_total}</strong><span>{item.risk_level}</span></td><td><strong>{item.opened_alerts_count}</strong><span>{item.critical_alerts_count} critico(s)</span></td><td>{item.workflow_events_count}</td><td>{item.incidents_count}</td><td>{item.signal_summary}</td></tr>)) : <tr><td colSpan={6}>{detailLoading ? 'Carregando historico do jogador...' : 'Nenhum bucket de historico foi consolidado para este jogador.'}</td></tr>}</tbody></table></div>
          </article>
        ) : (
          <article className="admin-section-card"><div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Foco</p><h2>Selecione um jogador para inspecionar o score</h2></div><span className="admin-chip">{detailLoading ? 'Carregando' : 'Sem foco ativo'}</span></div></article>
        )}

        {alertWorkflow ? (
          <article className="admin-section-card">
            <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Workflow</p><h2>{alertWorkflow.alert.title}</h2></div><span className="admin-chip">{alertWorkflow.alert.status}</span></div>
            <div className="admin-kpi-grid"><article className="admin-kpi-item"><span>Severidade</span><strong>{alertWorkflow.alert.severity}</strong></article><article className="admin-kpi-item"><span>Score</span><strong>{alertWorkflow.alert.risk_score}</strong></article><article className="admin-kpi-item"><span>Fonte</span><strong>{formatScoreSource(alertWorkflow.alert.source_type)}</strong></article><article className="admin-kpi-item"><span>Incidente</span><strong>{alertWorkflow.incident ? alertWorkflow.incident.status : 'SEM LINK'}</strong></article></div>
            <div className="admin-detail-grid"><div className="admin-detail-pair"><span>Alvo</span><strong>{alertWorkflow.alert.target_type}</strong></div><div className="admin-detail-pair"><span>Entidade</span><strong>{alertWorkflow.alert.player_name || alertWorkflow.alert.club_name || alertWorkflow.alert.table_name || '-'}</strong></div><div className="admin-detail-pair"><span>Responsavel</span><strong>{alertWorkflow.alert.assigned_to_name || 'Sem responsavel'}</strong></div><div className="admin-detail-pair"><span>Aberto</span><strong>{formatDateTime(alertWorkflow.alert.opened_at)}</strong></div><div className="admin-detail-pair"><span>Resolvido</span><strong>{formatDateTime(alertWorkflow.alert.resolved_at)}</strong></div><div className="admin-detail-pair"><span>Fechado</span><strong>{formatDateTime(alertWorkflow.alert.closed_at)}</strong></div></div>
            <div className="admin-filter-actions">{alertWorkflow.alert.player_user_id ? <button type="button" className="admin-secondary-button" onClick={() => void loadPlayerDetails(alertWorkflow.alert.player_user_id as string)} disabled={detailLoading}>{detailLoading && selectedPlayerId === alertWorkflow.alert.player_user_id ? 'Carregando jogador...' : 'Abrir jogador'}</button> : null}<button type="button" className="admin-secondary-button" onClick={() => { void loadAlertWorkflow(alertWorkflow.alert.id); void loadAlertEvidence(alertWorkflow.alert.id); }} disabled={workflowLoading || evidenceLoading}>{workflowLoading || evidenceLoading ? 'Atualizando...' : 'Recarregar workflow'}</button></div>
            <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Acoes</p><h2>Ownership e encerramento</h2></div>{alertWorkflow.actions.promote_on_assign ? <span className="admin-chip">Vai promover bootstrap</span> : null}</div>
            <div className="admin-filter-grid"><label className="admin-filter-field"><span>Motivo para assumir</span><textarea className="admin-search-input" rows={3} value={assignReason} onChange={(event) => setAssignReason(event.target.value)} placeholder="Explique por que este alerta esta entrando em investigacao." /></label><label className="admin-filter-field"><span>Encerramento</span><select className="admin-search-input" value={resolution} onChange={(event) => setResolution(event.target.value as 'RESOLVED' | 'DISMISSED')}><option value="RESOLVED">RESOLVED</option><option value="DISMISSED">DISMISSED</option></select></label><label className="admin-filter-field"><span>Motivo para encerrar</span><textarea className="admin-search-input" rows={3} value={resolveReason} onChange={(event) => setResolveReason(event.target.value)} placeholder="Documente a decisao operacional para auditoria." /></label></div>
            <div className="admin-filter-actions"><button type="button" className="admin-secondary-button" onClick={() => void handleAssign()} disabled={!alertWorkflow.actions.can_assign || actionLoading === 'assign'}>{actionLoading === 'assign' ? 'Assumindo...' : 'Assumir / reatribuir'}</button><button type="button" className="admin-secondary-button" onClick={() => void handleResolve()} disabled={!alertWorkflow.actions.can_resolve || actionLoading === 'resolve'}>{actionLoading === 'resolve' ? 'Encerrando...' : formatResolutionLabel(resolution)}</button></div>
            <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Evidencias do alerta</p><h2>Contexto operacional</h2></div></div>
            <div className="admin-note-stack">{workflowEvidence.length ? workflowEvidence.map((entry) => (<div key={entry} className="admin-note-chip">{entry}</div>)) : <div className="admin-note-chip">Nenhuma evidencia estruturada foi anexada a este alerta.</div>}</div>
            <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Bundle forense</p><h2>Correlacao consolidada do alerta</h2></div><span className="admin-chip">{evidenceLoading ? 'Atualizando' : `${alertEvidence?.summary.total_sections || 0} secao(oes)`}</span></div>
            <div className="admin-note-stack">{alertEvidence?.sections.length ? alertEvidence.sections.map((section) => (<div key={section.key} className="admin-section-card"><div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Bundle</p><h2>{section.title}</h2></div><span className="admin-chip">{section.items.length} item(ns)</span></div><div className="admin-table-wrap"><table className="admin-table"><thead><tr><th>Label</th><th>Valor</th><th>Quando</th></tr></thead><tbody>{section.items.map((item) => (<tr key={`${section.key}-${item.label}-${item.value}`}><td>{item.label}</td><td>{item.value}</td><td>{formatDateTime(item.occurred_at)}</td></tr>))}</tbody></table></div></div>)) : <div className="admin-note-chip">{evidenceLoading ? 'Carregando bundle forense...' : 'Nenhuma correlacao adicional foi consolidada para este alerta.'}</div>}</div>
            <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Timeline</p><h2>Eventos do workflow</h2></div><span className="admin-chip">{alertWorkflow.timeline.length} evento(s)</span></div>
            <div className="admin-note-stack">{alertWorkflow.timeline.length ? alertWorkflow.timeline.map((event) => (<div key={event.id} className="admin-note-chip">{formatWorkflowEvent(event)}</div>)) : <div className="admin-note-chip">Nenhum evento estruturado foi encontrado ainda para este alerta.</div>}</div>
          </article>
        ) : (
          <article className="admin-section-card"><div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Workflow</p><h2>Selecione um alerta para acompanhar a trilha</h2></div><span className="admin-chip">{workflowLoading ? 'Carregando' : 'Sem alerta selecionado'}</span></div></article>
        )}
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Mesas</p><h2>Superficie de risco por mesa</h2></div><span className="admin-chip">{surfaceLoading ? 'Atualizando' : `${tableRisk?.summary.total_items || 0} mesa(s)`}</span></div>
          <div className="admin-kpi-grid"><article className="admin-kpi-item"><span>High risk</span><strong>{tableRisk?.summary.high_risk_tables || 0}</strong></article><article className="admin-kpi-item"><span>Criticas</span><strong>{tableRisk?.summary.critical_tables || 0}</strong></article><article className="admin-kpi-item"><span>Incidents</span><strong>{tableRisk?.summary.open_incidents_count || 0}</strong></article><article className="admin-kpi-item"><span>Persistidas</span><strong>{tableRisk?.summary.persisted_items || 0}</strong></article></div>
          <div className="admin-table-wrap"><table className="admin-table"><thead><tr><th>Mesa</th><th>Score</th><th>Jogadores</th><th>Alertas</th><th>Devices</th><th>Ultima atividade</th><th>Historico</th></tr></thead><tbody>{tableRisk?.items.length ? tableRisk.items.map((item) => (<tr key={item.table_id} className={selectedTableId === item.table_id ? 'admin-table__row--selected' : ''}><td><strong>{item.table_name}</strong><span>{item.club_name} | {item.game_type}</span></td><td><strong>{item.risk_score}</strong><span>{item.risk_level}</span></td><td><strong>{item.high_risk_players_count}</strong><span>{item.escalated_players_count} escalado(s)</span></td><td><strong>{item.open_alerts_count}</strong><span>{item.open_incidents_count} incident(s)</span></td><td><strong>{item.shared_device_pairs_count}</strong><span>{item.stale_sessions_30d} stale</span></td><td><strong>{formatDateTime(item.last_activity_at)}</strong><span>{item.score_source === 'PERSISTED' ? 'Com correlacao' : 'Bootstrap'}</span></td><td><button type="button" className="admin-link-chip admin-link-chip--button" onClick={() => void loadTableHistory(item.table_id)} disabled={surfaceDetailLoading && selectedTableId === item.table_id}>{surfaceDetailLoading && selectedTableId === item.table_id ? 'Carregando...' : 'Abrir'}</button></td></tr>)) : <tr><td colSpan={7}>{surfaceLoading ? 'Carregando superficie por mesa...' : 'Nenhuma mesa entrou na superficie de risco.'}</td></tr>}</tbody></table></div>
          <div className="admin-note-stack">{tableRisk?.notes?.map((entry) => (<div key={entry} className="admin-note-chip">{entry}</div>))}</div>
        </article>
        <article className="admin-section-card">
          <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Clubs</p><h2>Superficie de risco por club</h2></div><span className="admin-chip">{surfaceLoading ? 'Atualizando' : `${clubRisk?.summary.total_items || 0} club(s)`}</span></div>
          <div className="admin-kpi-grid"><article className="admin-kpi-item"><span>High risk</span><strong>{clubRisk?.summary.high_risk_clubs || 0}</strong></article><article className="admin-kpi-item"><span>Criticos</span><strong>{clubRisk?.summary.critical_clubs || 0}</strong></article><article className="admin-kpi-item"><span>Incidents</span><strong>{clubRisk?.summary.open_incidents_count || 0}</strong></article><article className="admin-kpi-item"><span>Persistidos</span><strong>{clubRisk?.summary.persisted_items || 0}</strong></article></div>
          <div className="admin-table-wrap"><table className="admin-table"><thead><tr><th>Club</th><th>Score</th><th>Jogadores</th><th>Financeiro</th><th>Alertas</th><th>Ultima atividade</th><th>Historico</th></tr></thead><tbody>{clubRisk?.items.length ? clubRisk.items.map((item) => (<tr key={item.club_id} className={selectedClubId === item.club_id ? 'admin-table__row--selected' : ''}><td><strong>{item.club_name}</strong><span>{item.club_code}</span></td><td><strong>{item.risk_score}</strong><span>{item.risk_level}</span></td><td><strong>{item.high_risk_players_count}</strong><span>{item.escalated_players_count} escalado(s)</span></td><td><strong>{item.failed_payment_events_30d}</strong><span>{item.pending_withdrawals_count} saque(s) pendente(s)</span></td><td><strong>{item.open_alerts_count}</strong><span>{item.open_incidents_count} incident(s)</span></td><td><strong>{formatDateTime(item.last_activity_at)}</strong><span>{item.active_tables} mesa(s) ativas</span></td><td><button type="button" className="admin-link-chip admin-link-chip--button" onClick={() => void loadClubHistory(item.club_id)} disabled={surfaceDetailLoading && selectedClubId === item.club_id}>{surfaceDetailLoading && selectedClubId === item.club_id ? 'Carregando...' : 'Abrir'}</button></td></tr>)) : <tr><td colSpan={7}>{surfaceLoading ? 'Carregando superficie por club...' : 'Nenhum club entrou na superficie de risco.'}</td></tr>}</tbody></table></div>
          <div className="admin-note-stack">{clubRisk?.notes?.map((entry) => (<div key={entry} className="admin-note-chip">{entry}</div>))}</div>
        </article>
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Historico da mesa</p><h2>{tableHistory?.profile.table_name || 'Selecione uma mesa'}</h2></div><span className="admin-chip">{tableHistory?.current_score.total_score || 0}</span></div>
          <div className="admin-table-wrap"><table className="admin-table"><thead><tr><th>Bucket</th><th>Score</th><th>Alertas</th><th>Workflow</th><th>Incidents</th><th>Sinal</th></tr></thead><tbody>{tableHistory?.items.length ? tableHistory.items.map((item) => (<tr key={item.bucket_start}><td>{item.bucket_label}</td><td><strong>{item.score_total}</strong><span>{item.risk_level}</span></td><td><strong>{item.opened_alerts_count}</strong><span>{item.critical_alerts_count} critico(s)</span></td><td>{item.workflow_events_count}</td><td>{item.incidents_count}</td><td>{item.signal_summary}</td></tr>)) : <tr><td colSpan={6}>{surfaceDetailLoading ? 'Carregando historico da mesa...' : 'Nenhuma mesa em foco para historico.'}</td></tr>}</tbody></table></div>
          <div className="admin-note-stack">{tableHistory?.notes?.map((entry) => (<div key={entry} className="admin-note-chip">{entry}</div>))}</div>
        </article>
        <article className="admin-section-card">
          <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Historico do club</p><h2>{clubHistory?.profile.club_name || 'Selecione um club'}</h2></div><span className="admin-chip">{clubHistory?.current_score.total_score || 0}</span></div>
          <div className="admin-table-wrap"><table className="admin-table"><thead><tr><th>Bucket</th><th>Score</th><th>Alertas</th><th>Workflow</th><th>Incidents</th><th>Sinal</th></tr></thead><tbody>{clubHistory?.items.length ? clubHistory.items.map((item) => (<tr key={item.bucket_start}><td>{item.bucket_label}</td><td><strong>{item.score_total}</strong><span>{item.risk_level}</span></td><td><strong>{item.opened_alerts_count}</strong><span>{item.critical_alerts_count} critico(s)</span></td><td>{item.workflow_events_count}</td><td>{item.incidents_count}</td><td>{item.signal_summary}</td></tr>)) : <tr><td colSpan={6}>{surfaceDetailLoading ? 'Carregando historico do club...' : 'Nenhum club em foco para historico.'}</td></tr>}</tbody></table></div>
          <div className="admin-note-stack">{clubHistory?.notes?.map((entry) => (<div key={entry} className="admin-note-chip">{entry}</div>))}</div>
        </article>
      </section>

      <section className="admin-grid admin-grid--two">
        <article className="admin-section-card">
          <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Relacoes</p><h2>Grafo operacional do jogador em foco</h2></div><span className="admin-chip">{playerRelationships?.summary.total_related_players || 0} conexao(oes)</span></div>
          <div className="admin-kpi-grid"><article className="admin-kpi-item"><span>Alta suspeita</span><strong>{playerRelationships?.summary.high_suspicion_edges || 0}</strong></article><article className="admin-kpi-item"><span>Mesmo device</span><strong>{playerRelationships?.summary.shared_device_edges || 0}</strong></article><article className="admin-kpi-item"><span>Mesmo club</span><strong>{playerRelationships?.summary.shared_club_edges || 0}</strong></article><article className="admin-kpi-item"><span>Persistidas</span><strong>{playerRelationships?.summary.persisted_edges || 0}</strong></article></div>
          <div className="admin-table-wrap"><table className="admin-table"><thead><tr><th>Jogador relacionado</th><th>Suspeita</th><th>Devices</th><th>Clubs</th><th>Mesas / maos</th><th>Fonte</th><th>Detalhe</th></tr></thead><tbody>{playerRelationships?.items.length ? playerRelationships.items.map((item) => (<tr key={item.edge_id}><td><strong>{item.related_user_name}</strong><span>{item.related_user_email || '-'}</span></td><td><strong>{item.suspicion_score}</strong><span>{item.net_flow_reais ? `Flow BRL ${item.net_flow_reais.toFixed(2)}` : 'Sem flow direto'}</span></td><td>{item.shared_devices_count}</td><td><strong>{item.shared_clubs_count}</strong><span>{item.club_code || item.club_name || '-'}</span></td><td><strong>{item.shared_tables_count}</strong><span>{item.shared_hands_count} maos</span></td><td>{item.source_type}</td><td><button type="button" className="admin-link-chip admin-link-chip--button" onClick={() => void loadPlayerDetails(item.related_user_id)} disabled={detailLoading && selectedPlayerId === item.related_user_id}>{detailLoading && selectedPlayerId === item.related_user_id ? 'Carregando...' : 'Inspecionar'}</button></td></tr>)) : <tr><td colSpan={7}>Nenhuma relacao relevante foi consolidada para o jogador em foco.</td></tr>}</tbody></table></div>
        </article>
        <article className="admin-section-card">
          <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Alertas do jogador</p><h2>Fila recente associada ao foco atual</h2></div></div>
          <div className="admin-table-wrap"><table className="admin-table"><thead><tr><th>Titulo</th><th>Severidade</th><th>Status</th><th>Origem</th><th>Aberto</th></tr></thead><tbody>{playerRisk?.recent_alerts.length ? playerRisk.recent_alerts.map((alert) => (<tr key={alert.id}><td><strong>{alert.title}</strong><span>{alert.category}</span></td><td><strong>{alert.severity}</strong><span>Score {alert.risk_score}</span></td><td>{alert.status}</td><td>{alert.source}</td><td>{formatDateTime(alert.opened_at)}</td></tr>)) : <tr><td colSpan={5}>Nenhum alerta recente ligado a este jogador.</td></tr>}</tbody></table></div>
        </article>
      </section>

      <section className="admin-section-card">
        <div className="admin-section-card__header"><div><p className="admin-section-eyebrow">Notas</p><h2>Limites e observacoes desta etapa</h2></div></div>
        <div className="admin-note-stack">{(overview?.notes || []).map((note, index) => (<div key={`overview-note-${index}`} className="admin-note-chip">{note}</div>))}{(ruleEngine?.notes || []).map((note, index) => (<div key={`rule-engine-note-${index}`} className="admin-note-chip">{note}</div>))}{(alertsPayload?.notes || []).map((note, index) => (<div key={`alerts-note-${index}`} className="admin-note-chip">{note}</div>))}{(playerRisk?.notes || []).map((note, index) => (<div key={`player-note-${index}`} className="admin-note-chip">{note}</div>))}{(playerHistory?.notes || []).map((note, index) => (<div key={`player-history-note-${index}`} className="admin-note-chip">{note}</div>))}{(tableHistory?.notes || []).map((note, index) => (<div key={`table-history-note-${index}`} className="admin-note-chip">{note}</div>))}{(clubHistory?.notes || []).map((note, index) => (<div key={`club-history-note-${index}`} className="admin-note-chip">{note}</div>))}{(playerRelationships?.notes || []).map((note, index) => (<div key={`relationships-note-${index}`} className="admin-note-chip">{note}</div>))}{(alertWorkflow?.notes || []).map((note, index) => (<div key={`workflow-note-${index}`} className="admin-note-chip">{note}</div>))}{(alertEvidence?.notes || []).map((note, index) => (<div key={`evidence-note-${index}`} className="admin-note-chip">{note}</div>))}</div>
      </section>
    </div>
  );
};

export default SecurityPage;
